import { caseStoreConst } from './constants';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import {
  CaseData,
  SubTables,
  SubTable,
  Fields,
  FieldValue,
  StoreData,
  StoreMetadata,
  StoreDomainMeta,
  EntityPath,
  CompositeKeys,
  StoreField,
  StoreItemPath,
  Entity,
  ComplexValue,
  ConstraintResponseType,
  ValidationMessageLevel
} from 'app/types-shared';
import { toISODatewithoutTimezone } from 'app/infrastructure/data/date';
import { toAbsoluteUrl } from 'app/infrastructure/hosting';

/*
 * Flatten CaseData to store format
 */
const mapCaseData = (data: CaseData) => ({
  ...(data.caseType && {
    [caseStoreConst.caseType]: [codeItemToStoreValue(data.caseType)],
    [caseStoreConst.caseContext]: [codeItemToStoreValue(data.caseContext)]
  })
});

const mapValues = (key: string, field: FieldValue) =>
  field && field.values
    ? {
        [key]: field.values
          .map(v => codeItemToStoreValue(v))
          .filter(v => v !== undefined)
      }
    : ({} as { [x: string]: StoreField[] });

const mapFields = (fields: Fields) =>
  fields
    ? Object.keys(fields).reduce(
        (item: { [x: string]: StoreField[] }, key) => ({
          ...item,
          ...mapValues(key, fields[key])
        }),
        {}
      )
    : {};

function mapSubtables(
  subtables: SubTables,
  rootPath: StoreItemPath[]
): StoreData {
  return subtables
    ? Object.keys(subtables).reduce(
        (storeData: StoreData, key) =>
          subtables[key]
            ? [
                ...storeData,
                ...subtables[key].reduce(
                  (item, subtable) => [
                    ...item,
                    ...mapDataToItem(subtable, rootPath, key)
                  ],
                  []
                )
              ]
            : storeData,
        []
      )
    : [];
}

function mapDataToItem(
  data: CaseData | SubTable,
  rootPath: StoreItemPath[],
  dataKey: string
) {
  if (!data) {
    return [];
  }

  // null/undefined are not legal as primary key value in Synergi Life.
  const isValidPrimaryKey =
    data.primaryKey && typeof data.primaryKey.v === 'number';

  // calculate current path
  const path = isValidPrimaryKey && [
    ...rootPath,
    { [dataKey]: data.primaryKey.v }
  ];

  let primaryKey = undefined;

  //Add readonly primary key. Preserve code information that is lost when added to the path.
  if (isValidPrimaryKey) {
    const value = data.primaryKey.v;
    const description = data.primaryKey.d;
    const longDescription = data.primaryKey.ld;
    const categoryId = data.primaryKey.ctg;
    const icon = data.primaryKey.i && toAbsoluteUrl(data.primaryKey.i);

    primaryKey = {
      value,
      ...(description && { description }),
      ...(longDescription && { longDescription }),
      ...(categoryId && { categoryId }),
      ...(icon && { icon })
    } as ComplexValue<number>;
  }

  const fields = {
    ...(path && { id: path }),
    ...(primaryKey && { primaryKey }),
    ...mapFields(data.fields),
    //...(path && mapLinks(data.subTables)),
    ...mapCaseData(data as CaseData)
  };

  // create current element and add fields
  return Object.keys(fields).length !== 0
    ? [fields, ...mapSubtables(data.subTables, path)]
    : [];
}

/**
 * Converts case data sent over wire to flat redux store format
 *
 * @param {CaseData} caseData case data in wire format
 * @returns {StoreData} store state
 */
export const caseDataToStore = (caseData: CaseData): StoreData =>
  mapDataToItem(caseData, [], caseStoreConst.tblTrans);

/*
 * Flatten CaseData metadata to store format
 */
const mapDomainMetadata = (data: CaseData): StoreDomainMeta => {
  if (!data || !data.metaData) {
    return {};
  }

  let metaData = mapReadOnlyDomains({}, data);
  metaData = mapRequiredFields(metaData, data);
  metaData = mapHiddenDomains(metaData, data);
  metaData = mapFailedConstraints(metaData, data);

  return metaData;
};

const mapReadOnlyDomains = (metaData: StoreDomainMeta, data: CaseData): StoreDomainMeta => {
  const readOnlyDomains = data.metaData.readOnlyDomains || [];
  return readOnlyDomains.reduce((memo, current) => {
    memo[current.domainId] = {
      ...memo[current.domainId],
      readOnly: {
        globalMatch: !current.entityPaths,
        entityPaths: entityPathsToCompositeKeys(current.entityPaths)
      }
    };

    return memo;
  }, metaData);
};

const mapRequiredFields = (metaData: StoreDomainMeta, data: CaseData): StoreDomainMeta => {
  const requiredFieldDomainsValue = data.metaData.requiredFieldDomains || [];
  const requiredFieldDomainsForStatusChangeValue =
    data.metaData.requiredFieldDomainsForStatusChange || [];

  const requiredFieldDomains = [
    ...requiredFieldDomainsValue,
    ...requiredFieldDomainsForStatusChangeValue.filter(
      d => requiredFieldDomainsValue.indexOf(d) < 0
    )
  ];

  return requiredFieldDomains.reduce((memo, current) => {
    memo[current.domainId] = {
      ...memo[current.domainId],
      requiredField: {
        globalMatch: !current.entityPaths,
        entityPaths: entityPathsToCompositeKeys(current.entityPaths)
      }
    };
    return memo;
  }, metaData);
};

const mapHiddenDomains = (metaData: StoreDomainMeta, data: CaseData): StoreDomainMeta => {
  const hiddenDomainsValue = data.metaData.hiddenDomains || [];
  return hiddenDomainsValue.reduce((memo, current) => {
    memo[current.domainId] = {
      ...memo[current.domainId],
      hidden: {
        globalMatch: !current.entityPaths,
        entityPaths: entityPathsToCompositeKeys(current.entityPaths)
      }
    };
    return memo;
  }, metaData);
};

const mapFailedConstraints = (metaData: StoreDomainMeta, data: CaseData): StoreDomainMeta => {
  function getLevel(responseType: ConstraintResponseType): ValidationMessageLevel {
    return responseType === ConstraintResponseType.Error ?
      ValidationMessageLevel.Error : 
      ValidationMessageLevel.Warning;
  }

  const failedDomainConstraintsValue = data.metaData.failedDomainConstraints || [];
  return failedDomainConstraintsValue.reduce((memo, current) => {
    memo[current.domainId] = {
      ...memo[current.domainId],
      validations: [
        ...(memo[current.domainId] && memo[current.domainId].validations || []),
        {
          globalMatch: !current.entityPaths,
          entityPaths: entityPathsToCompositeKeys(
            current.entityPaths && 
            current.entityPaths.map(p => p.map(e => ({ tbl: e.entityType, pk: e.entityId })))
          ),
          level: getLevel(current.responseType),
          label: current.domainText,
          message: current.message
        }
      ]
    };
    return memo;
  }, metaData);
};

function entityPathsToCompositeKeys(
  entityPaths: EntityPath[][]
): CompositeKeys[] {
  if (!entityPaths) {
    return undefined;
  }

  return entityPaths.map(ep =>
    ep.map(row => ({
      [row.tbl]: row.pk
    }))
  );
}

/**
 * Extracts metadata from case data sent over wire to flat redux store format.
 * Metadata is merging lazy props surch as readonly.
 *
 * @param {CaseData} caseData case data in wire format
 * @returns {StoreMetadata} store metadata
 */
export const caseMetadataToStore = (caseData: CaseData): StoreMetadata => ({
  domains: mapDomainMetadata(caseData)
});

export const valueToCaseValue = (value: string | number | Date) => {
  if (value instanceof Date) {
    return {
      ...value,
      toJSON: toISODatewithoutTimezone(value)
    };
  }
  return value;
};

/*
 * Unflatten store format to CaseData
 */

const mapItemToCaseData = (item: Entity) =>
  Object.keys(item).reduce((data: CaseData, key) => {
    const value = item[key] as StoreField[];
    let field;

    switch (key) {
      case caseStoreConst.id:
        const lastElementIndex = value.length - 1;
        const lastPathElement = item[key][lastElementIndex] as StoreItemPath;
        const lastPathValue = lastPathElement[Object.keys(lastPathElement)[0]];
        field = typeof lastPathValue === 'number' && {
          primaryKey: { v: lastPathValue }
        };
        break;

      case caseStoreConst.caseType:
        field = { caseType: { v: Array.isArray(value) && value[0].value } };
        break;

      case caseStoreConst.caseContext:
        field = { caseContext: { v: Array.isArray(value) && value[0].value } };
        break;

      case caseStoreConst.operation:
        field = { operation: value };
        break;

      case caseStoreConst.primaryKey:
        field = {};
        break;

      default:
        if (value && typeof value[0] === 'number') {
          break;
        }
        field = {
          fields: {
            ...(data.fields || {}),
            [key]: {
              values: Array.isArray(value) && value.map(v => ({ v: v.value }))
            }
          }
        };
        break;
    }

    return {
      ...data,
      ...field
    };
  }, {});

/**
 * Converts store data into wire case format
 *
 * @param {StoreData} storeData store data
 * @returns {CaseData} case data in wire format
 */
export const storeToCaseData = (storeData: StoreData): CaseData =>
  storeData
    ? storeData.reduce(
        (caseData, item) => {
          let ret: CaseData = caseData;

          let current: CaseData | SubTable = ret;
          const fullPath = (item && item.id) || [{}];

          for (const path of fullPath) {
            const table = Object.keys(path)[0];

            if (table === caseStoreConst.tblTrans || !table) {
              if (
                fullPath !== item.id ||
                path === item.id[item.id.length - 1]
              ) {
                ret = {
                  ...ret,
                  ...mapItemToCaseData(item)
                };
              }
              current = ret;
            } else {
              let currentsubtable =
                (current.subTables &&
                  current.subTables[table] &&
                  current.subTables[table].find(
                    s => s.primaryKey && s.primaryKey.v === path[table]
                  )) ||
                ({} as SubTable);

              if (path === item.id[item.id.length - 1]) {
                currentsubtable = {
                  ...currentsubtable,
                  ...mapItemToCaseData(item)
                };
              }

              const othersubtables =
                (current.subTables &&
                  current.subTables[table] &&
                  current.subTables[table].filter(
                    s => !s.primaryKey || s.primaryKey.v !== path[table]
                  )) ||
                [];

              current.subTables = {
                ...(current.subTables || {}),
                [table]: [...othersubtables, currentsubtable]
              };
              current = currentsubtable;
            }
          }

          return ret;
        },
        {} as CaseData
      )
    : ({} as CaseData);
